import React, {useEffect, useRef, useState} from "react";
import {resetAppBarBottomContent, updateMetadata} from "store/metadata/slice";
import Cart from "view/apps/Exhibitor/pages/Cart";
import {useQuickOrderId} from "hooks/show/order";
import {useDispatch} from "react-redux";
import {useCurrentShow, useCurrentShowId, useShowOrder} from "hooks";
import {HeaderType, useHeaderView} from "store/header/header.store";
import {CardOrder, OrderStatus, RawOrder} from "ts/api/order";
import {useLocation, useNavigate} from "react-router-dom";
import {LOGIN_PATH} from "view/routes";
import QuickOrderHeader from "view/components/AppBar/components/QuickOrderHeader";
import BrandedShowLayout from "view/components/BrandedShowLayout";
import {
  Currency,
  ExhibitorPairStatus,
  IExhibitorPair,
  IExhibitorUserConnection,
  PermissionPackage,
  Roles,
} from "@evennt-tech/api-v2-core";
import {CurrencyContext} from "view/context/CurrencyContext";
import {AxiosError} from "axios";

import useCurrentExhibitorPairId from "hooks/useCurrentExhibitorPairId";
import {apiV2} from "api/firebase";
import {useUserWithClaims} from "hooks/useUserWithClaims";
import {PaymentMethodType} from "ts/api/paymentMethod";
import stripeService from "api/stripe";
import useRedirectToOrderPage from "view/apps/Exhibitor/pages/Cart/components/Payment/components/useRedirectToOrderPage";
import {
  AlreadyPaid,
  ExpiredLink,
  InvalidUrl,
  WrongExhibitor,
  WrongUserRole,
} from "./NoAccessModals";
import {
  CheckingPaymentStatus,
  ResumeOrRestartPayment,
} from "./PaymentReconciliation";

type InitialOrderState = {
  value?: RawOrder;
  isLoading: boolean;
};

type ReconcileState =
  | {status: "idle"}
  | {status: "checking"}
  | {
      status: "requiresAction";
      clientSecret: string;
      cardLast4?: string;
      cardBrand?: string;
    }
  | {status: "resolved"};

function QuickCart() {
  const showId = useCurrentShowId();
  const exhibitorPairId = useCurrentExhibitorPairId();
  const redirectToOrderPage = useRedirectToOrderPage(showId, exhibitorPairId);
  const {loading: isLoadingUser, user} = useUserWithClaims();

  const [show, isLoadingShow] = useCurrentShow({
    enabled: !!user?.id,
  });

  const quickOrderId = useQuickOrderId();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [initialOrder, setInitialOrder] = useState<InitialOrderState>({
    isLoading: true,
  });
  const [reconcile, setReconcile] = useState<ReconcileState>({
    status: "idle",
  });
  const reconcileStartedRef = useRef(false);
  const [order, isLoadingOrder] = useShowOrder({
    orderId: quickOrderId,
    isAlive: true,
  });

  const [exhibitorPair, setExhibitorPair] = useState<IExhibitorPair | null>(
    null
  );
  const [
    exhibitorConnection,
    setExhibitorConnection,
  ] = useState<IExhibitorUserConnection | null>(null);
  const [isExhibitorPairLoading, setIsExhibitorPairLoading] = useState(true);
  const [isWrongExhibitorPair, setIsWrongExhibitorPair] = useState(false);

  useEffect(() => {
    if (!showId || !exhibitorPairId || user?.role !== Roles.EXHIBITOR) {
      setIsExhibitorPairLoading(false);
      return;
    }

    const load = async () => {
      setIsExhibitorPairLoading(true);
      setIsWrongExhibitorPair(false);
      try {
        const response = await apiV2.exhibitor.pair.getOne({
          showId,
          exhibitorPairId,
        });

        let connection: IExhibitorUserConnection | null = null;

        if (response?.stand?.standId) {
          connection = await apiV2.exhibitor.userConnection
            .getOne({
              exhibitorPairId,
              showId,
              exhibitorUserId: user?.id,
              oldFirebaseStandId: response?.stand?.standId,
            })
            .catch(() => null);
        }

        setExhibitorConnection(connection);
        setExhibitorPair(response);
      } catch (e) {
        if ((e as AxiosError).response?.status === 403) {
          setIsWrongExhibitorPair(true);
        }
        setExhibitorConnection(null);
        setExhibitorPair(null);
      } finally {
        setIsExhibitorPairLoading(false);
      }
    };

    load();
  }, [showId, exhibitorPairId, user?.role]);

  useEffect(() => {
    if (isLoadingOrder) {
      return;
    }

    if (!initialOrder.value && order) {
      setInitialOrder({value: order, isLoading: false});
    }
  }, [isLoadingOrder, order, initialOrder.value]);

  useEffect(() => {
    const isPendingCardPayment =
      initialOrder.value?.paymentDetails.status === OrderStatus.processing &&
      initialOrder.value?.paymentType === PaymentMethodType.cardPayment;

    if (
      initialOrder.isLoading ||
      reconcileStartedRef.current ||
      !showId ||
      !quickOrderId
    ) {
      return;
    }
    if (!isPendingCardPayment) {
      reconcileStartedRef.current = true;
      setReconcile({status: "resolved"});
      return;
    }

    let cancelled = false;
    reconcileStartedRef.current = true;
    setReconcile({status: "checking"});

    stripeService
      .reconcileCardOrder({showId, orderId: quickOrderId})
      .then((result) => {
        if (cancelled) {
          return;
        }
        if (result.outcome === "requiresAction") {
          setReconcile({
            status: "requiresAction",
            clientSecret: result.clientSecret,
            cardLast4: result.cardLast4,
            cardBrand: result.cardBrand,
          });
          return;
        }
        if (result.outcome === "awaitingPayment") {
          setInitialOrder((prev) =>
            prev.value
              ? {
                  ...prev,
                  value: {
                    ...prev.value,
                    paymentDetails: {
                      ...prev.value.paymentDetails,
                      status: OrderStatus.awaitingPayment,
                    },
                  } as RawOrder,
                }
              : prev
          );
        }
        setReconcile({status: "resolved"});
      })
      .catch(() => {
        if (!cancelled) {
          setReconcile({status: "resolved"});
        }
      });

    return () => {
      cancelled = true;
    };
  }, [initialOrder.isLoading, initialOrder.value, showId, quickOrderId]);

  const handleReconciliationResolved = (
    nextStatus: OrderStatus.paid | OrderStatus.awaitingPayment
  ) => {
    if (nextStatus === OrderStatus.paid && quickOrderId) {
      // A payment completed just now behaves like any other successful
      // checkout — land on the order itself, not our "already paid" screen
      // (that one's for reopening a link to an order paid earlier).
      redirectToOrderPage(quickOrderId, initialOrder.value?.orderGroupId);
      return;
    }

    setInitialOrder((prev) =>
      prev.value
        ? {
            ...prev,
            value: {
              ...prev.value,
              paymentDetails: {
                ...prev.value.paymentDetails,
                status: nextStatus,
              },
            } as RawOrder,
          }
        : prev
    );
    setReconcile({status: "resolved"});
  };

  useEffect(() => {
    if (!quickOrderId) {
      return;
    }
    dispatch(
      updateMetadata({
        hideSidebar: true,
        appBarBottomContent: {},
        showBar: null,
      })
    );
    return () => {
      dispatch(resetAppBarBottomContent());
    };
  }, [dispatch, quickOrderId]);

  const {setType} = useHeaderView();

  useEffect(() => {
    if (!quickOrderId) {
      return;
    }
    setType({type: HeaderType.QUICK_ORDER, orderId: quickOrderId});
    return () => setType({type: HeaderType.STANDARD});
  }, [quickOrderId, setType]);

  if (isLoadingUser) {
    return null;
  }

  if (!user?.id) {
    const {pathname, search} = location;
    navigate(
      `${LOGIN_PATH}?redirectPathAfterLogin=${encodeURIComponent(
        `${pathname}${search}`
      )}`
    );
  }

  if (!!user?.role && user?.role !== Roles.EXHIBITOR) {
    return <WrongUserRole />;
  }

  if (
    initialOrder.isLoading ||
    (isLoadingOrder && !initialOrder.value) ||
    isExhibitorPairLoading ||
    isLoadingShow ||
    !show
  ) {
    return null;
  }

  const isShopEnabled =
    show.permissionPackages.includes(PermissionPackage.VENDR) &&
    show.isShopEnabled;
  if (!isShopEnabled) {
    return <InvalidUrl />;
  }

  if (reconcile.status === "checking") {
    return <CheckingPaymentStatus />;
  }

  if (reconcile.status === "requiresAction" && quickOrderId) {
    const cardOrder = initialOrder.value as CardOrder | undefined;
    return (
      <ResumeOrRestartPayment
        showId={showId}
        orderId={quickOrderId}
        paymentIntentId={cardOrder?.paymentIntentId ?? ""}
        clientSecret={reconcile.clientSecret}
        cardLast4={reconcile.cardLast4}
        currency={show.currency}
        products={cardOrder?.products ?? []}
        onResolved={handleReconciliationResolved}
      />
    );
  }

  if (initialOrder.value?.paymentDetails?.status === OrderStatus.paid) {
    return <AlreadyPaid />;
  }

  if (isWrongExhibitorPair) {
    return <WrongExhibitor />;
  }

  const isExpiredLink =
    initialOrder.value?.paymentDetails?.status !==
      OrderStatus.awaitingPayment ||
    !exhibitorPair ||
    exhibitorPair.status === ExhibitorPairStatus.ARCHIVED ||
    order?.paymentDetails.status === OrderStatus.cancelled ||
    initialOrder.value?.paymentType === PaymentMethodType.bankTransfer;

  if (isExpiredLink) {
    return <ExpiredLink />;
  }

  const hasNoConnection = !exhibitorConnection;

  if (hasNoConnection) {
    return <WrongExhibitor />;
  }

  return (
    <CurrencyContext.Provider value={show?.currency || Currency.gbp}>
      <BrandedShowLayout
        showId={showId}
        header={<QuickOrderHeader />}
        containerStyle={{marginTop: "132px", height: "auto"}}
      >
        <Cart />
      </BrandedShowLayout>
    </CurrencyContext.Provider>
  );
}

export default QuickCart;
